import { useState, useEffect } from "react";
import {
  createComment,
  updateComment,
  deleteComment,
  getCommentsByOrderId,
} from "../../../../ApiCalls/mantisFlowsManangment";

// Helper function to generate initials from name
const getInitials = (name) => {
  if (!name) return "U";
  const names = name.split(" ");
  if (names.length === 1) return names[0].charAt(0).toUpperCase();
  return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
};

// Helper function to generate a consistent color based on name
const getAvatarColor = (name) => {
  if (!name) return "bg-blue-500";

  const colors = [
    "bg-blue-500",
    "bg-green-500",
    "bg-purple-500",
    "bg-pink-500",
    "bg-indigo-500",
    "bg-yellow-500",
    "bg-red-500",
    "bg-teal-500",
    "bg-orange-500",
    "bg-cyan-500",
  ];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = (now - date) / (1000 * 60 * 60);

  if (diffInHours < 0) {
    return "justo ahora";
  } else if (diffInHours < 1) {
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));
    if (diffInMinutes <= 0) {
      return "justo ahora";
    }
    return `hace ${diffInMinutes} min`;
  } else if (diffInHours < 24) {
    return `hace ${Math.floor(diffInHours)}h`;
  } else {
    return date.toLocaleDateString("es-ES", {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  }
};

export const CommentsSection = ({
  data,
  currentUser,
  addLog,
  selectedStep,
  clientId,
}) => {
  const [commentText, setCommentText] = useState("");
  const [comments, setComments] = useState([]);
  const [commentError, setCommentError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  console.log("🔍comments data:", data, currentUser);

  // Load comments when component mounts or when data changes
  useEffect(() => {
    const loadComments = async () => {
      if (!clientId || !data?.id) return;

      setIsLoadingComments(true);
      setCommentError(null);

      try {
        const response = await getCommentsByOrderId(clientId, data.id);
        if (response.success) {
          setComments(response.data || []);
        } else {
          setCommentError("No se pudieron cargar los comentarios");
        }
      } catch (error) {
        console.error("Error al cargar comentarios:", error);
        setCommentError("Error al cargar los comentarios");
      } finally {
        setIsLoadingComments(false);
      }
    };

    loadComments();
  }, [clientId, data?.id]);

  const handleAddComment = async () => {
    if (!commentText.trim() || !clientId || !data?.id) return;

    setIsSubmitting(true);
    setCommentError(null);

    try {
      const user_name =
        currentUser?.firstName + " " + currentUser?.lastName || "Usuario";
      const response = await createComment(
        clientId,
        data.id,
        commentText,
        user_name
      );

      if (response.success) {
        setComments((prev) => [response.data, ...prev]);
        setCommentText("");
        addLog &&
          addLog(`new comment added by ${user_name}`, "comment", selectedStep);
      } else {
        setCommentError("No se pudo guardar el comentario");
      }
    } catch (error) {
      console.error("Error al añadir comentario:", error);
      setCommentError("Error al guardar el comentario");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditComment = async (commentId) => {
    if (!commentText.trim() || !clientId || !data?.id) return;

    setIsSubmitting(true);
    setCommentError(null);

    try {
      const user_name = currentUser?.name || "Usuario";
      const response = await updateComment(
        clientId,
        commentId,
        commentText,
        user_name,
        data.id
      );

      if (response.success) {
        setComments((prev) =>
          prev.map((c) => (c.id === commentId ? response.data : c))
        );
        setCommentText("");
        setEditingCommentId(null);
        addLog &&
          addLog(
            `comment edited by ${user_name}`,
            "comment_edit",
            selectedStep
          );
      } else {
        setCommentError("No se pudo actualizar el comentario");
      }
    } catch (error) {
      console.error("Error al editar comentario:", error);
      setCommentError("Error al actualizar el comentario");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!clientId) return;

    if (
      !window.confirm("¿Estás seguro de que deseas eliminar este comentario?")
    ) {
      return;
    }

    try {
      const user_name = currentUser?.name || "Usuario";
      const response = await deleteComment(
        clientId,
        commentId,
        user_name,
        data.id
      );

      if (response.success) {
        setComments((prev) => prev.filter((c) => c.id !== commentId));
        addLog &&
          addLog(
            `comment deleted by ${user_name}`,
            "comment_delete",
            selectedStep
          );
      } else {
        setCommentError("No se pudo eliminar el comentario");
      }
    } catch (error) {
      console.error("Error al eliminar comentario:", error);
      setCommentError("Error al eliminar el comentario");
    }
  };

  const startEditingComment = (comment) => {
    setEditingCommentId(comment.id);
    setCommentText(comment.comment || "");
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg p-6 shadow-sm border border-slate-200 dark:border-slate-700">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
          <svg
            className="w-4 h-4 text-blue-600 dark:text-blue-400"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <h4 className="text-xl font-semibold text-slate-800 dark:text-slate-100">
          Comentarios
        </h4>
        {comments.length > 0 && (
          <span className="bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 text-xs font-medium px-2.5 py-0.5 rounded-full">
            {comments.length}
          </span>
        )}
      </div>

      {commentError && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-4">
          <div className="flex items-center gap-2">
            <svg
              className="w-4 h-4 text-red-500"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-red-600 dark:text-red-400 text-sm">
              {commentError}
            </span>
          </div>
        </div>
      )}

      {/* Comment input moved above comments */}
      <div className="border-b border-slate-200 dark:border-slate-700 pb-6 mb-6">
        <div className="flex gap-2">
          <textarea
            className="flex-1 border border-slate-300 dark:border-slate-600 rounded-xl p-3 text-sm text-slate-800 dark:text-slate-100 dark:bg-slate-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none transition-all duration-200 placeholder:text-slate-400 dark:placeholder:text-slate-500 min-h-[48px]"
            rows={1}
            placeholder="Escribe un comentario..."
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onInput={(e) => {
              // Auto-resize textarea based on content
              e.target.style.height = "auto";
              e.target.style.height =
                Math.max(48, e.target.scrollHeight) + "px";
            }}
            disabled={isSubmitting}
          />
          {editingCommentId ? (
            <div className="flex gap-2">
              <button
                className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handleEditComment(editingCommentId)}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Guardando..." : "Guardar"}
              </button>
              <button
                className="bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-300 dark:hover:bg-slate-600 focus:ring-2 focus:ring-slate-500 focus:ring-offset-2 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => {
                  setEditingCommentId(null);
                  setCommentText("");
                }}
                disabled={isSubmitting}
              >
                Cancelar
              </button>
            </div>
          ) : (
            <button
              className="bg-blue-600 text-white p-3 rounded-xl hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center shadow-sm hover:shadow-md h-[48px] w-[48px] flex-shrink-0"
              onClick={handleAddComment}
              disabled={isSubmitting || !commentText.trim()}
              title="Enviar comentario"
            >
              {isSubmitting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              ) : (
                <svg
                  className="w-5 h-5 transform rotate-45"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                </svg>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Comments section */}
      <div className="space-y-4">
        {isLoadingComments ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-slate-500 dark:text-slate-400">
              Cargando comentarios...
            </span>
          </div>
        ) : comments.length === 0 ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-slate-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              No hay comentarios aún.
            </p>
            <p className="text-slate-400 dark:text-slate-500 text-xs mt-1">
              Sé el primero en comentar
            </p>
          </div>
        ) : (
          comments.map((comment) => {
            const initials = getInitials(comment.user_name);
            const avatarColor = getAvatarColor(comment.user_name);

            return (
              <div
                key={comment.id}
                className="group bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors duration-200"
              >
                <div className="flex items-start gap-3">
                  {/* Avatar with initials */}
                  <div
                    className={`${avatarColor} w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm`}
                  >
                    <span className="text-white font-semibold text-sm">
                      {initials}
                    </span>
                  </div>

                  {/* Comment content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-semibold text-slate-800 dark:text-slate-200 text-sm">
                        {comment.user_name || "Usuario"}
                      </span>
                      <span className="text-xs text-slate-400 dark:text-slate-500">
                        {formatDate(comment.created_at)}
                      </span>
                    </div>
                    <div className="text-slate-700 dark:text-slate-300 text-sm leading-relaxed">
                      {comment.comment}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
